import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Text } from "@chakra-ui/react";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { GALLERY_LAYOUTS } from "./constants";
import { mapGalleryItems } from "./mapGalleryItems";
import { GalleryFullscreenButton } from "./FullScreenButton";
import { useGallerySwipeToDismiss } from "./useGallerySwipeToDismiss";
import "./FursatookImageGallery.scss";

function renderGallerySlide(item) {
  return (
    <div className="fursatook-gallery-stage">
      <img
        className="fursatook-gallery-slide__image image-gallery-image"
        src={item.original}
        alt={item.originalAlt || ""}
        loading={item.loading || "lazy"}
      />
    </div>
  );
}

const FursatookImageGallery = forwardRef(function FursatookImageGallery(
  {
    images,
    items: itemsProp,
    layout = GALLERY_LAYOUTS.vehicle,
    onScreenChange,
    swipeHint = "Swipe down to close",
    showSwipeHint = true,
    showThumbnailsInFullscreen = true,
    showIndexInFullscreen = true,
    lazyLoad = true,
    additionalClass = "",
    showThumbnails: showThumbnailsProp,
    showIndex: showIndexProp,
    showFullscreenButton: showFullscreenButtonProp,
    renderFullscreenButton,
    ...imageGalleryProps
  },
  ref
) {
  const wrapperRef = useRef(null);
  const galleryRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useImperativeHandle(ref, () => galleryRef.current, []);

  const items = useMemo(
    () => itemsProp ?? mapGalleryItems(images),
    [images, itemsProp]
  );

  const layoutClass = Object.values(GALLERY_LAYOUTS).includes(layout)
    ? `fursatook-image-gallery--${layout}`
    : "fursatook-image-gallery--vehicle";

  const handleScreenChange = useCallback(
    (fullscreen) => {
      setIsFullscreen(fullscreen);
      onScreenChange?.(fullscreen);
    },
    [onScreenChange]
  );

  const getTargetElement = useCallback(
    () => wrapperRef.current?.querySelector(".image-gallery.fullscreen-modal"),
    []
  );

  const dismissFullscreen = useCallback(() => {
    galleryRef.current?.toggleFullScreen?.();
  }, []);

  useGallerySwipeToDismiss({
    enabled: isFullscreen,
    getTargetElement,
    onDismiss: dismissFullscreen,
  });

  const defaultRenderFullscreenButton = useCallback(
    (onClick, fullscreen) => (
      <GalleryFullscreenButton
        onClick={onClick}
        isFullscreen={fullscreen}
        label={items.length}
      />
    ),
    [items.length]
  );

  const showThumbnails =
    showThumbnailsProp ?? (showThumbnailsInFullscreen && isFullscreen);
  const showIndex = showIndexProp ?? (showIndexInFullscreen && isFullscreen);
  const showFullscreenButton =
    showFullscreenButtonProp ?? items.length > 0;

  return (
    <Box
      ref={wrapperRef}
      className={`fursatook-image-gallery ${layoutClass} ${
        isFullscreen ? "is-fullscreen" : ""
      } ${additionalClass}`.trim()}
      position="relative"
    >
      {isFullscreen && showSwipeHint && (
        <Text className="gallery-swipe-hint" aria-hidden="true">
          {swipeHint}
        </Text>
      )}
      <ImageGallery
        ref={galleryRef}
        items={items}
        useBrowserFullscreen={false}
        lazyLoad={lazyLoad}
        showPlayButton={false}
        showThumbnails={showThumbnails}
        showIndex={showIndex}
        showFullscreenButton={showFullscreenButton}
        renderItem={renderGallerySlide}
        onScreenChange={handleScreenChange}
        renderFullscreenButton={
          renderFullscreenButton ?? defaultRenderFullscreenButton
        }
        {...imageGalleryProps}
      />
    </Box>
  );
});

export default FursatookImageGallery;
